{/* Add this section to your Marketing.jsx page - place it before the footer */}

import { useState } from 'react'
import { HiShieldCheck, HiLockClosed, HiCloud, HiKey, HiEye, HiTrash } from 'react-icons/hi'

const faqs = [
  {
    question: 'Where is my code stored?',
    answer: 'Your code is uploaded directly to Google Cloud Storage (your bucket) in Mumbai. Inflynx backend never stores or runs your code - it only orchestrates the deployment process.',
  },
  {
    question: 'What happens if Inflynx goes down?',
    answer: 'Your deployed apps continue running on Google Cloud Run completely independently. You can\'t deploy new versions, but existing apps are unaffected.',
  },
  {
    question: 'Do you use my code for AI training?',
    answer: 'No. We explicitly do not use your code for AI/ML training, share data with third parties, or sell usage analytics.',
  },
  {
    question: 'How do I delete my data?',
    answer: 'Use `flynt deployments delete <id>` for deployments, `flynt projects delete <name>` for projects, or contact support to delete your account.',
  },
  {
    question: 'Can I access my data directly?',
    answer: 'Yes! You own all GCP resources. Manage Cloud Run services, download code from GCS, export databases via GCP Console or gcloud CLI.',
  },
  {
    question: 'What compliance do you have?',
    answer: 'GCP infrastructure is SOC 2 Type II, ISO 27001, ISO 27017, ISO 27018, GDPR, HIPAA, and PCI DSS compliant.',
  },
  {
    question: 'Who can access my data?',
    answer: 'Only you. JWT tokens (7-day expiry) + Google OAuth 2.0. All data encrypted with AES-256 at rest and TLS 1.3 in transit.',
  },
  {
    question: 'What if deployment fails?',
    answer: 'Build failures save logs. Deploy failures trigger auto-rollback. Database crashes have <60s failover with no data loss.',
  },
  {
    question: 'Is there vendor lock-in?',
    answer: 'None. You own all GCP resources (Cloud Run, Cloud SQL, GCS). Manage everything via GCP Console without Inflynx.',
  },
  {
    question: 'How secure is my data?',
    answer: 'Enterprise-grade: AES-256 encryption, bcrypt hashing, VPC-private deployments, gVisor sandboxing, minimal IAM permissions.',
  },
]

const securityFeatures = [
  {
    icon: <HiShieldCheck className="w-8 h-8" />,
    title: 'Enterprise Security',
    description: 'JWT + Google OAuth 2.0, TLS 1.3 encryption, bcrypt password hashing',
  },
  {
    icon: <HiLockClosed className="w-8 h-8" />,
    title: 'Your Data is Yours',
    description: 'AES-256 encryption at rest, stored in Mumbai (asia-south1)',
  },
  {
    icon: <HiCloud className="w-8 h-8" />,
    title: 'Zero Downtime',
    description: 'Apps run independently on Cloud Run even if we go down',
  },
  {
    icon: <HiKey className="w-8 h-8" />,
    title: 'No Vendor Lock-in',
    description: 'You own all GCP resources - manage via Console anytime',
  },
  {
    icon: <HiEye className="w-8 h-8" />,
    title: 'Transparent Operations',
    description: 'Every action logged, viewable via CLI or GCP Console',
  },
  {
    icon: <HiTrash className="w-8 h-8" />,
    title: 'Easy Deletion',
    description: 'Delete deployments or entire account with one command',
  },
]

export default function SecuritySection() {
  const [openFaq, setOpenFaq] = useState(null)
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Enterprise-Grade Security & Transparency
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We understand that trusting a platform with your code is a big decision.
            Here's exactly how we protect your data.
          </p>
        </div>

        {/* Security Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {securityFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow"
            >
              <div className="text-blue-600 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Architecture Diagram */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16 border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">
            How Your Code Flows Through Inflynx
          </h3>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            {/* Step 1 */}
            <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200 flex-1 max-w-xs text-center">
              <div className="text-3xl mb-2">💻</div>
              <div className="font-semibold text-slate-900">Your CLI</div>
              <div className="text-sm text-slate-600">flynt deploy</div>
            </div>
            
            <div className="text-slate-300 text-3xl">→</div>
            
            {/* Step 2 */}
            <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200 flex-1 max-w-xs text-center">
              <div className="text-3xl mb-2">🚀</div>
              <div className="font-semibold text-slate-900">Inflynx Backend</div>
              <div className="text-sm text-slate-600">Orchestration only</div>
            </div>
            
            <div className="text-slate-300 text-3xl">→</div>
            
            {/* Step 3 */}
            <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200 flex-1 max-w-xs text-center">
              <div className="text-3xl mb-2">☁️</div>
              <div className="font-semibold text-slate-900">Your GCP Project</div>
              <div className="text-sm text-slate-600">You own everything</div>
            </div>
          </div>
          
          {/* Final destinations */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-lg font-semibold text-slate-900">Cloud Run</div>
              <div className="text-sm text-slate-600">Your deployed app runs here</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-lg font-semibold text-slate-900">Cloud SQL</div>
              <div className="text-sm text-slate-600">Your database (isolated)</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-lg font-semibold text-slate-900">GCS Bucket</div>
              <div className="text-sm text-slate-600">Your code (encrypted)</div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-center text-white">
            <div className="text-4xl font-bold mb-2">AES-256</div>
            <div className="text-blue-100">Encryption Standard</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-center text-white">
            <div className="text-4xl font-bold mb-2">99.9%</div>
            <div className="text-green-100">Uptime SLA</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-center text-white">
            <div className="text-4xl font-bold mb-2">7 Days</div>
            <div className="text-purple-100">Backup Retention</div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  <span className={`transform transition-transform text-2xl ${openFaq === index ? 'rotate-45' : ''}`}>
                    +
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-slate-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-lg text-slate-600 mb-6">
            Have more questions about security?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/security"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              View Security Documentation
            </a>
            <a
              href="mailto:security@inflynx.cloud"
              className="inline-flex items-center justify-center px-8 py-3 border border-slate-300 text-base font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors"
            >
              Email Security Team
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
